import type {
  ContactDoNotUpdate,
  ContactInfoUpdate,
  ContactInterestUpdate,
} from '@sola/schema/dotcom/contacts/update.ts';
import type {
  GraphQLContactPreference,
  GraphQLSupporterAddress,
  GraphQLWritingPreference,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { UserError } from '@sola/shared-utils/errors-user.ts';
import { userErrorBadParam } from '@sola/shared-utils/errors/misc.ts';
import {
  httpPatchWithStatus,
  httpPutWithStatus,
  isErrorResponse,
  isSuccessStatus,
  isUserError,
} from '@sola/shared-utils/httpClient.ts';
import type { ErrorResponse } from '@sola/shared-utils/httpClient.ts';

import { MY_API_BASE } from '../constants.ts';

export type AccountSettingsMutationError = UserError | UserError[];

export interface AddressUpdateResponse {
  success: boolean;
  data?: GraphQLSupporterAddress | unknown;
  message?: string;
}

export type ContactInfoPayload = ContactInfoUpdate;
export type ContactPreferencePayload =
  | ContactInterestUpdate['preferences']
  | ContactDoNotUpdate['preferences'];

export interface ContactInfoUpdateResponse {
  success: boolean;
  data?: ContactInfoPayload | unknown;
  message?: string;
}

const routeAccountSettingsContact = (supporterId: string, contactId: string) =>
  `${MY_API_BASE}/supporters/${supporterId}/contacts/${contactId}`;

const routeAccountSettingsAddress = (supporterId: string) =>
  `${MY_API_BASE}/supporters/${supporterId}/address`;

const routeSupporterLetterPreferences = (supporterId: string) =>
  `${MY_API_BASE}/supporters/${supporterId}/letter-preferences`;

/**
 * Update a supporter's mailing address.
 */
export const updateSupporterAddress = async ({
  globalId,
  address,
}: {
  globalId: string;
  address: Omit<GraphQLSupporterAddress, 'isUndeliverable'>;
}): Promise<AddressUpdateResponse | AccountSettingsMutationError> => {
  if (!globalId) {
    throw userErrorBadParam('globalId');
  }

  if (!address) {
    throw userErrorBadParam('address');
  }

  const { response, statusCode } = await httpPutWithStatus<
    AddressUpdateResponse | ErrorResponse
  >(routeAccountSettingsAddress(globalId), address, { signRequest: true });

  if (statusCode !== 200 || !response || isErrorResponse(response)) {
    throw new Error('Failed to update address');
  }

  return response as AddressUpdateResponse;
};

/**
 * Update a supporter's contact information (and optionally preferences).
 */
export const updateSupporterContactInfo = async ({
  contactId,
  globalId,
  contactInfo,
}: {
  contactId: string;
  globalId: string;
  contactInfo: ContactInfoPayload;
}): Promise<ContactInfoUpdateResponse | AccountSettingsMutationError> => {
  if (!contactId) {
    throw userErrorBadParam('contactId');
  }
  if (!globalId) {
    throw userErrorBadParam('globalId');
  }

  if (!contactInfo) {
    throw userErrorBadParam('contactInfo');
  }

  const { response, statusCode } = await httpPatchWithStatus<
    ContactInfoUpdateResponse | ErrorResponse
  >(routeAccountSettingsContact(globalId, contactId), contactInfo, {
    signRequest: true,
  });

  if (!isSuccessStatus(statusCode) || !response || isErrorResponse(response)) {
    throw new Error('Failed to update contact info');
  }

  return response as ContactInfoUpdateResponse;
};

export interface ContactPreferenceUpdateResponse {
  success: boolean;
  data?: GraphQLContactPreference | unknown;
  message?: string;
}

/**
 * Update a supporter's contact preferences.
 * Uses the same PATCH /contacts/{contactId} endpoint — the backend
 * mapper handles preference fields when present on the payload.
 */
export const updateSupporterContactPreferences = async ({
  contactId,
  globalId,
  preferences,
}: {
  contactId: string;
  globalId: string;
  preferences: ContactPreferencePayload;
}): Promise<ContactPreferenceUpdateResponse | AccountSettingsMutationError> => {
  if (!contactId) {
    throw userErrorBadParam('contactId');
  }
  if (!globalId) {
    throw userErrorBadParam('globalId');
  }

  if (!preferences) {
    throw userErrorBadParam('preferences');
  }

  const { response, statusCode } = await httpPatchWithStatus<
    ContactPreferenceUpdateResponse | ErrorResponse
  >(
    routeAccountSettingsContact(globalId, contactId),
    { preferences },
    {
      signRequest: true,
    },
  );

  if (!isSuccessStatus(statusCode) || !response || isErrorResponse(response)) {
    throw new Error('Failed to update contact preferences');
  }

  return response as ContactPreferenceUpdateResponse;
};

export interface LetterWritingPreferencesUpdateResponse {
  success: boolean;
  data?: { writingPreferences: GraphQLWritingPreference[] };
  message?: string;
}

/**
 * Update a supporter's letter writing preferences.
 */
export const updateSupporterLetterPreferences = async ({
  globalId,
  writingPreferences,
}: {
  globalId: string;
  writingPreferences: GraphQLWritingPreference[];
}): Promise<
  LetterWritingPreferencesUpdateResponse | AccountSettingsMutationError
> => {
  if (globalId == null || globalId.trim().length === 0) {
    throw userErrorBadParam('globalId');
  }

  if (!Array.isArray(writingPreferences)) {
    throw userErrorBadParam('writingPreferences');
  }

  const { response, statusCode } = await httpPutWithStatus<
    LetterWritingPreferencesUpdateResponse | ErrorResponse
  >(
    routeSupporterLetterPreferences(globalId),
    { writingPreferences },
    { signRequest: true },
  );

  if (statusCode !== 200 || !response || isErrorResponse(response)) {
    if (isUserError(response)) {
      throw response;
    }
    throw new Error('Failed to update letter writing preferences');
  }

  return response as LetterWritingPreferencesUpdateResponse;
};
